import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiUserPlus, FiMail, FiX, FiSend, FiShield, FiUser, FiCheck, 
  FiSearch, FiInfo, FiAlertCircle, FiKey, FiUsers, FiLock
} = FiIcons;

const mockParticipants = [
  { id: 1, name: 'Dr. Emily Carter', email: 'emily.carter@specialty.com', role: 'Owner', permissions: 'Full Access', status: 'Active', avatar: 'https://randomuser.me/api/portraits/women/44.jpg' },
  { id: 2, name: 'Dr. Alan Grant', email: 'alan.grant@dino.com', role: 'Specialist', permissions: 'View, Edit & Comment', status: 'Active', avatar: 'https://randomuser.me/api/portraits/men/44.jpg' },
  { id: 3, name: 'John Doe', email: 'john.doe@patient.com', role: 'Patient', permissions: 'View & Comment', status: 'Invited', avatar: 'https://randomuser.me/api/portraits/men/32.jpg' },
  { id: 4, name: 'Compliance Officer', email: 'compliance@audit.com', role: 'Viewer', permissions: 'View Only', status: 'Active', avatar: 'https://randomuser.me/api/portraits/men/36.jpg' },
];

const mockUsers = [
  { id: 101, name: 'Dr. Sarah Johnson', email: 'sarah.johnson@clinic.com', avatar: 'https://randomuser.me/api/portraits/women/65.jpg' },
  { id: 102, name: 'Dr. Michael Brown', email: 'michael.brown@hospital.com', avatar: 'https://randomuser.me/api/portraits/men/67.jpg' },
  { id: 103, name: 'Nurse Jane Smith', email: 'jane.smith@clinic.com', avatar: 'https://randomuser.me/api/portraits/women/68.jpg' },
  { id: 104, name: 'Admin Lisa White', email: 'lisa.white@admin.com', avatar: 'https://randomuser.me/api/portraits/women/70.jpg' },
];

const RoleBadge = ({ role }) => {
  const roleClasses = {
    Owner: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    Collaborator: 'bg-blue-100 text-blue-800 border-blue-200',
    Specialist: 'bg-green-100 text-green-800 border-green-200',
    Patient: 'bg-purple-100 text-purple-800 border-purple-200',
    Viewer: 'bg-gray-100 text-gray-800 border-gray-200',
  };
  return (
    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${roleClasses[role] || ''}`}>
      {role}
    </span>
  );
};

const ParticipantsManager = ({ dealId }) => {
  const [participants, setParticipants] = useState(mockParticipants);
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [addMode, setAddMode] = useState('existing'); // 'existing' or 'new'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserForm, setNewUserForm] = useState({ email: '', firstName: '', lastName: '' });
  const [selectedRole, setSelectedRole] = useState('Specialist');
  const [selectedPermissions, setSelectedPermissions] = useState('View & Comment');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredUsers = mockUsers.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddParticipant = async () => {
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (addMode === 'existing' && selectedUser) {
      // Check if already participant
      if (participants.some(p => p.email === selectedUser.email)) {
        alert('This user is already a participant');
        setIsSubmitting(false);
        return;
      }

      const newParticipant = {
        id: participants.length + 1,
        name: selectedUser.name,
        email: selectedUser.email,
        role: selectedRole,
        permissions: selectedPermissions,
        status: 'Active',
        avatar: selectedUser.avatar,
      };
      setParticipants([...participants, newParticipant]);
      console.log('Added existing user:', newParticipant);
    } else if (addMode === 'new') {
      // Check if email exists in system
      const existingUser = mockUsers.find(u => u.email === newUserForm.email);
      if (existingUser) {
        alert('This email already exists in the system. Please use "Select Existing User" mode.');
        setIsSubmitting(false);
        return;
      }

      const tempPassword = Math.random().toString(36).slice(-16);
      const newParticipant = {
        id: participants.length + 1,
        name: `${newUserForm.firstName} ${newUserForm.lastName}`,
        email: newUserForm.email,
        role: selectedRole,
        permissions: selectedPermissions,
        status: 'Invited',
        avatar: `https://ui-avatars.com/api/?name=${newUserForm.firstName}+${newUserForm.lastName}`,
      };
      setParticipants([...participants, newParticipant]);
      console.log('Created new user with temp password:', tempPassword);
    }

    // Reset and close
    setIsSubmitting(false);
    resetModal();
  };

  const resetModal = () => {
    setInviteModalOpen(false);
    setAddMode('existing');
    setSearchTerm('');
    setSelectedUser(null);
    setNewUserForm({ email: '', firstName: '', lastName: '' });
    setSelectedRole('Specialist');
    setSelectedPermissions('View & Comment');
  };

  const isFormValid = () => {
    if (addMode === 'existing') {
      return selectedUser !== null;
    } else {
      return newUserForm.email && newUserForm.firstName && newUserForm.lastName;
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-800">Participants</h3>
          <p className="text-sm text-gray-500 mt-1">{participants.length} members</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setInviteModalOpen(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-lg shadow-sm transition-colors"
        >
          <SafeIcon icon={FiUserPlus} />
          Add Participant
        </motion.button>
      </div>

      <div className="space-y-3">
        {participants.map((p, index) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="relative">
                <img src={p.avatar} alt={p.name} className="w-12 h-12 rounded-full ring-2 ring-gray-100" />
                {p.role === 'Owner' && (
                  <div className="absolute -bottom-1 -right-1 bg-indigo-500 text-white p-1 rounded-full">
                    <SafeIcon icon={FiShield} className="w-3 h-3" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-gray-900 truncate">{p.name}</p>
                <p className="text-sm text-gray-500 truncate">{p.email}</p>
                <p className="text-xs text-gray-400 mt-0.5">{p.permissions}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <RoleBadge role={p.role} />
              <span className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${
                p.status === 'Active' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                  p.status === 'Active' ? 'bg-green-500' : 'bg-yellow-500'
                }`}></div>
                {p.status}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add Participant Modal */}
      <AnimatePresence>
        {isInviteModalOpen && (
          <motion.div
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={resetModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 flex justify-between items-center border-b border-gray-200 sticky top-0 bg-white z-10">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Add Participant</h3>
                  <p className="text-sm text-gray-500 mt-1">Invite a user to collaborate on this dealroom</p>
                </div>
                <button 
                  onClick={resetModal} 
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Mode Toggle */}
                <div className="grid grid-cols-2 gap-3 p-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setAddMode('existing')}
                    className={`flex items-center justify-center gap-2 py-3 px-4 rounded-md font-semibold transition-all ${
                      addMode === 'existing'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <SafeIcon icon={FiUsers} className="w-5 h-5" />
                    Select Existing User
                  </button>
                  <button
                    onClick={() => setAddMode('new')}
                    className={`flex items-center justify-center gap-2 py-3 px-4 rounded-md font-semibold transition-all ${
                      addMode === 'new'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <SafeIcon icon={FiUserPlus} className="w-5 h-5" />
                    Create New User
                  </button>
                </div>

                {/* Existing User Mode */}
                {addMode === 'existing' && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-2 block">Search Users</label>
                      <div className="relative">
                        <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input 
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search by name or email..." 
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                        />
                      </div>
                    </div>

                    {/* User List */}
                    <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map(user => (
                          <div
                            key={user.id}
                            onClick={() => setSelectedUser(user)}
                            className={`flex items-center gap-3 p-3 cursor-pointer transition-all border-b border-gray-100 last:border-b-0 ${
                              selectedUser?.id === user.id
                                ? 'bg-blue-50 border-l-4 border-l-blue-600'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full" />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{user.name}</p>
                              <p className="text-sm text-gray-500 truncate">{user.email}</p>
                            </div>
                            {selectedUser?.id === user.id && (
                              <div className="bg-blue-600 text-white p-1 rounded-full">
                                <SafeIcon icon={FiCheck} className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          <SafeIcon icon={FiSearch} className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No users found</p>
                        </div>
                      )}
                    </div>

                    {selectedUser && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                        <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-blue-900">Selected: {selectedUser.name}</p>
                          <p className="text-xs text-blue-700 mt-1">{selectedUser.email}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* New User Mode */}
                {addMode === 'new' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                      <SafeIcon icon={FiInfo} className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-900">Creating a New User</p>
                        <p className="text-xs text-amber-700 mt-1">
                          A new user account will be created with a temporary password. The user will receive an invitation email to set their password.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div>
                        <label className="text-sm font-semibold text-gray-700 mb-2 block">Email Address *</label>
                        <div className="relative">
                          <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input 
                            type="email"
                            value={newUserForm.email}
                            onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                            placeholder="user@example.com" 
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-semibold text-gray-700 mb-2 block">First Name *</label>
                          <input 
                            type="text"
                            value={newUserForm.firstName}
                            onChange={(e) => setNewUserForm({...newUserForm, firstName: e.target.value})}
                            placeholder="John" 
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-gray-700 mb-2 block">Last Name *</label>
                          <input 
                            type="text"
                            value={newUserForm.lastName}
                            onChange={(e) => setNewUserForm({...newUserForm, lastName: e.target.value})}
                            placeholder="Doe" 
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Role Selection */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Assign Role</label>
                  <div className="relative">
                    <SafeIcon icon={FiShield} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select 
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>Owner</option>
                      <option>Collaborator</option>
                      <option>Specialist</option>
                      <option>Patient</option>
                      <option>Viewer</option>
                    </select>
                  </div>
                </div>

                {/* Permissions Selection */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Permissions</label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select 
                      value={selectedPermissions}
                      onChange={(e) => setSelectedPermissions(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>View Only</option>
                      <option>View & Comment</option>
                      <option>View, Edit & Comment</option>
                      <option>Full Access (Manage)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0">
                <button 
                  onClick={resetModal} 
                  className="py-2.5 px-5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <motion.button 
                  whileHover={{ scale: isFormValid() ? 1.02 : 1 }} 
                  whileTap={{ scale: isFormValid() ? 0.98 : 1 }}
                  onClick={handleAddParticipant}
                  disabled={!isFormValid() || isSubmitting}
                  className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2.5 px-6 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={addMode === 'new' ? FiUserPlus : FiSend} />
                      {addMode === 'new' ? 'Create & Add' : 'Add Participant'}
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ParticipantsManager;